import React from 'react';
import { X, Globe, TrendingUp, Award, RotateCcw } from 'lucide-react';
import { useApp } from '../../lib/AppContext';
import { Language, DifficultyLevel } from '../../types';

interface SettingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const LANGUAGE_INFO: Record<Language, { name: string; flag: string }> = {
  en: { name: 'English', flag: '🇺🇸' },
  es: { name: 'Español', flag: '🇪🇸' },
  fr: { name: 'Français', flag: '🇫🇷' },
  de: { name: 'Deutsch', flag: '🇩🇪' },
  it: { name: 'Italiano', flag: '🇮🇹' },
  pt: { name: 'Português', flag: '🇧🇷' },
  nl: { name: 'Nederlands', flag: '🇳🇱' },
  pl: { name: 'Polski', flag: '🇵🇱' },
};

const LEVEL_INFO = {
  1: { name: 'Beginner', emoji: '🌟', color: 'from-green-400 to-green-500' },
  2: { name: 'Easy', emoji: '⭐', color: 'from-blue-400 to-blue-500' },
  3: { name: 'Medium', emoji: '🏅', color: 'from-yellow-400 to-yellow-500' },
  4: { name: 'Hard', emoji: '🏆', color: 'from-orange-400 to-orange-500' },
  5: { name: 'Expert', emoji: '👑', color: 'from-red-400 to-red-500' },
};

export function SettingsDrawer({ isOpen, onClose }: SettingsDrawerProps) {
  const { userProfile, userProgress, setUserProfile } = useApp();

  if (!userProfile || !userProgress) return null;

  const handleLanguageChange = (language: Language) => {
    setUserProfile({
      ...userProfile,
      preferredLanguage: language,
    });
  };

  const handleResetProgress = () => {
    if (confirm('Are you sure you want to reset all progress? This cannot be undone.')) {
      localStorage.clear();
      window.location.reload();
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 z-40 ${
          isOpen ? 'opacity-50' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-50 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-6 text-white">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold">Settings</h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* User Info */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 mb-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="text-6xl">{userProfile.avatar}</div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">{userProfile.name}</h3>
                  <p className="text-gray-600">Learning Champion</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-4">
                <div className="bg-white rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-yellow-600">
                    {userProgress.totalScore}
                  </div>
                  <div className="text-xs text-gray-600">Score</div>
                </div>
                <div className="bg-white rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {userProgress.gamesPlayed}
                  </div>
                  <div className="text-xs text-gray-600">Games</div>
                </div>
                <div className="bg-white rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {userProgress.wordsLearned.length}
                  </div>
                  <div className="text-xs text-gray-600">Words</div>
                </div>
              </div>
            </div>

            {/* Language Selector */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Globe className="text-purple-600" size={20} />
                <h3 className="text-lg font-bold text-gray-800">Language</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {(Object.keys(LANGUAGE_INFO) as Language[]).map((lang) => {
                  const info = LANGUAGE_INFO[lang];
                  const isActive = userProfile.preferredLanguage === lang;
                  return (
                    <button
                      key={lang}
                      onClick={() => handleLanguageChange(lang)}
                      className={`
                        p-4 rounded-xl transition-all duration-200 text-left
                        ${
                          isActive
                            ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-lg scale-105'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                        }
                      `}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{info.flag}</span>
                        <span className="font-semibold">{info.name}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Level Progress */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="text-purple-600" size={20} />
                <h3 className="text-lg font-bold text-gray-800">Level Progress</h3>
              </div>
              <div className="space-y-2">
                {([1, 2, 3, 4, 5] as DifficultyLevel[]).map((level) => {
                  const info = LEVEL_INFO[level];
                  const progress = userProgress.levelProgress[level];
                  return (
                    <div
                      key={level}
                      className={`
                        p-4 rounded-xl ${
                          progress.unlocked ? 'bg-gradient-to-r ' + info.color : 'bg-gray-200'
                        } text-white
                      `}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{info.emoji}</span>
                          <div>
                            <div className="font-bold">{info.name}</div>
                            <div className="text-xs opacity-90">Level {level}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          {progress.unlocked ? (
                            <>
                              <div className="font-bold">{progress.completionPercentage}%</div>
                              <div className="text-xs opacity-90">
                                {'⭐'.repeat(progress.stars)}
                              </div>
                            </>
                          ) : (
                            <div className="text-sm opacity-75">🔒 Locked</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Achievements Preview */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Award className="text-purple-600" size={20} />
                <h3 className="text-lg font-bold text-gray-800">Achievements</h3>
              </div>
              <div className="bg-gray-100 rounded-xl p-4 text-center">
                <div className="text-4xl mb-2">🏆</div>
                <p className="text-gray-600 text-sm">
                  {userProgress.achievements.length} achievement
                  {userProgress.achievements.length !== 1 ? 's' : ''} unlocked!
                </p>
              </div>
            </div>

            {/* Reset Progress */}
            <div className="mb-6">
              <button
                onClick={handleResetProgress}
                className="w-full p-4 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl transition-colors flex items-center justify-center gap-2 font-semibold"
              >
                <RotateCcw size={20} />
                Reset All Progress
              </button>
              <p className="text-xs text-gray-500 text-center mt-2">
                This will delete all your progress and start fresh
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
